
import { useState } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Calendar, Loader2 } from "lucide-react";

const AccountBilling = () => {
  const t = useTranslations("consultantOnboarding.admin.billing");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      toast.success(t("updated"));
    }, 1000);
  };

  return (
    <div className="container max-w-3xl py-10">
      <h1 className="text-2xl font-bold mb-6">{t("title")}</h1>

      <Tabs defaultValue="billing" className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md mb-6">
          <TabsTrigger value="billing">{t("billingTab")}</TabsTrigger>
          <TabsTrigger value="subscription">{t("subscriptionTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>{t("paymentMethod")}</CardTitle>
              <CardDescription>
                {t("paymentMethodDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("nameOnCard")}</Label>
                    <Input id="name" placeholder={t("nameOnCardPlaceholder")} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="card-number">{t("cardNumber")}</Label>
                    <div className="relative">
                      <Input id="card-number" placeholder={t("cardNumberPlaceholder")} />
                      <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expiry">{t("expiry")}</Label>
                      <div className="relative">
                        <Input id="expiry" placeholder={t("expiryPlaceholder")} />
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cvc">{t("cvc")}</Label>
                      <Input id="cvc" placeholder={t("cvcPlaceholder")} />
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("updating")}
                    </>
                  ) : (
                    t("update")
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle>{t("planTitle")}</CardTitle>
              <CardDescription>
                {t("planDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border rounded-lg p-4">
                <h3 className="font-medium">{t("currentPlan")}</h3>
                <p className="text-sm text-muted-foreground">{t("planName")}</p>
                <div className="mt-2 flex justify-between items-center">
                  <span className="font-bold text-xl">{t("price")}</span>
                  <Button variant="outline" size="sm">{t("changePlan")}</Button>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-medium">{t("nextBilling")}</h3>
                <p className="text-sm text-muted-foreground">{t("nextBillingBody")}</p>
                <div className="mt-2">
                  <Button variant="outline" size="sm">{t("invoiceHistory")}</Button>
                </div>
              </div>

              <Button variant="destructive" className="w-full">{t("cancelSubscription")}</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountBilling;
